import editorialStyle from "../../../components/editorial/style.scss";

import Link, {
  Button,
  ButtonList,
  ButtonBar,
  ButtonDualGroup,
  ButtonGroup,
  LargeViewOnly,
  StickyButtons,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import ImageSet from "../../../components/image-set";
import Tags from "../../../components/tags";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import ReviewItem from "../../../components/review-item";
import NoWrap from "../../../components/no-wrap";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import Image from "../../../components/image";
import FixedTable from "../../../components/fixed-table";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

import AffiliateAd from "../../../components/affiliate-ad";

let headerInfo = lookupByUrl(namedRoutes.ecoflow.delta4.series);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={50}
      positionY={58}
      background="/images/backgrounds/delta-4-teaser.webp"
      titlePrefix="EcoFlow"
      title="DELTA 4 Series"
      subTitle="Generation 4 Power Stations"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          <strong>
            EcoFlow just officially announced its next-generation DELTA 4 Series
            power stations.
          </strong>{" "}
          Building on the groundwork of the DELTA 3 Series, which brought
          significant upgrades over the DELTA 2 Series, this next-generation
          release is primed to redefine portable power once again. With global
          launch plans yet to be announced, all eyes are on EcoFlow as
          additional models and regional availability details are announced. The
          launch follows closely behind the introduction of EcoFlow's new{" "}
          <strong>
            <Link href={namedRoutes.ecoflow.titanSeries}>TITAN Series</Link>
          </strong>
          , signaling a broader refresh of the company's portable power lineup.
        </p>

        <AffiliateDisclosure />

        <p>
          At launch, the <strong>DELTA 4 Series</strong> consists of two models:
          the{" "}
          <strong>
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-3000-gen4?variant=67651080093769"
            >
              DELTA 3000 Gen4
            </Link>
          </strong>{" "}
          and{" "}
          <strong>
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-3800-gen4?variant=67641627803721"
            >
              DELTA 3800 Gen4
            </Link>
          </strong>
          .
        </p>
      </Center>

      <Center>
        <SlugHeading tag="h2">Photos</SlugHeading>
        <p>
          The first <strong>official product photos</strong> provide an early
          look at the redesigned <strong>DELTA 4 Series</strong>, highlighting
          the updated form factor and feature layout of both models.
        </p>

        <strong>DELTA 3000 Gen4</strong>
        <br />

        <ImageSet
          size={5}
          border={true}
          images={[
            {
              lg: "/images/products/ecoflow/delta-4/delta-3000-gen4/front-right.webp",
              alt: "EcoFlow DELTA 3000 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3000-gen4/right.webp",
              alt: "EcoFlow DELTA 3000 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3000-gen4/front.webp",
              alt: "EcoFlow DELTA 3000 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3000-gen4/left.webp",
              alt: "EcoFlow DELTA 3000 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3000-gen4/back.webp",
              alt: "EcoFlow DELTA 3000 Gen4",
            },
          ]}
        />

        <strong>DELTA 3800 Gen4</strong>
        <br />

        <ImageSet
          size={5}
          border={true}
          images={[
            {
              lg: "/images/products/ecoflow/delta-4/delta-3800-gen4/front-right.webp",
              alt: "EcoFlow DELTA 3800 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3800-gen4/right.webp",
              alt: "EcoFlow DELTA 3800 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3800-gen4/front.webp",
              alt: "EcoFlow DELTA 3800 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3800-gen4/left.webp",
              alt: "EcoFlow DELTA 3800 Gen4",
            },
            {
              lg: "/images/products/ecoflow/delta-4/delta-3800-gen4/back.webp",
              alt: "EcoFlow DELTA 3800 Gen4",
            },
          ]}
        />

        <AffiliateAd brand="EcoFlow" size="billboard" />
      </Center>

      <Center>
        <SlugHeading tag="h2">Detailed Comparison</SlugHeading>
        <p>
          A side‑by‑side breakdown of the currently announced DELTA 4 Series
          models:
        </p>

        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th>DELTA 3000 Gen4</th>
                <th>DELTA 3800 Gen4</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Photo</strong>
                </th>
                <td>
                  <Image
                    src="/images/products/ecoflow/delta-4/delta-3000-gen4/front.webp"
                    alt="EcoFlow DELTA 3000 Gen4"
                    showAlt
                    maxHeight={200}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/ecoflow/delta-4/delta-3800-gen4/front.webp"
                    alt="EcoFlow DELTA 3800 Gen4"
                    showAlt
                    maxHeight={200}
                  />
                </td>
              </tr>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Links</strong>
                </th>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-3000-gen4?variant=67651080093769"
                      icon="EcoFlow"
                    >
                      Store
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-3800-gen4?variant=67641627803721"
                      icon="EcoFlow"
                    >
                      Store
                    </Button>
                  </ButtonBar>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Price</strong>
                </th>
                <td>$1,699</td>
                <td>$2,199</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Capacity</strong>
                </th>
                <td>3,072Wh</td>
                <td>3,763Wh</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Expandable</strong>
                </th>
                <td>No</td>
                <td>Yes</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>AC output</strong>
                </th>
                <td>
                  Total: 3,600W
                  <br />
                  Surge: 7,200W
                  {/* <br />
                  X-Boost: 3,800W */}
                </td>
                <td>
                  Total: 3,600W
                  <br />
                  Surge: 7,200W
                  {/* <br />
                  X-Boost: 3,800W */}
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>
                    <NoWrap>UPS switchover</NoWrap>
                  </strong>
                </th>
                <td>&lt;10ms</td>
                <td>&lt;10ms</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>AC ports</strong>
                </th>
                <td>
                  ×4 (20A each) <br />
                  ×1 (30A)
                </td>
                <td>
                  ×4 (20A each) <br />
                  ×1 (30A)
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>USB C</strong>
                </th>
                <td>3×</td>
                <td>3×</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>USB A</strong>
                </th>
                <td>1×</td>
                <td>1×</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>AC input</strong>
                </th>
                <td>1,500W</td>
                <td>1,500W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>
                    <NoWrap>Solar input 1</NoWrap>
                  </strong>
                </th>
                <td>1,000W</td>
                <td>1,000W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>
                    <NoWrap>Solar input 2</NoWrap>
                  </strong>
                </th>
                <td>--</td>
                <td>1,000W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Size (mm)</strong>
                </th>
                <td>
                  <NoWrap>477 × 289 × 282 mm</NoWrap>
                </td>
                <td>
                  <NoWrap>477 × 289 × 307 mm</NoWrap>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Weight</strong>
                </th>
                <td>
                  <NoWrap>26kg</NoWrap>
                </td>
                <td>
                  <NoWrap>30.6 kg</NoWrap>
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>
        <p class="caption">
          <strong>Note:</strong> There is conflicting information between
          EcoFlow's website, product manuals, and product photos.
        </p>
      </Center>

      <Center>
        <h2>Summary</h2>
        <p>
          The <strong>DELTA 4 Series</strong> represents EcoFlow's latest
          evolution in portable power. Together with the recently announced{" "}
          <strong>
            <Link href={namedRoutes.ecoflow.titanSeries}>TITAN Series</Link>
          </strong>
          , it marks{" "}
          <strong>
            the beginning of EcoFlow's next generation of power stations
          </strong>
          . Global availability has not yet been announced, but I'll continue
          monitoring the rollout and update this guide as additional
          specifications, regional models, and pricing information become
          available.
        </p>

        <p>
          Follow{" "}
          <Link external href="https://www.facebook.com/TysonsGearLab">
            Tyson's Gear Lab
          </Link>{" "}
          on Facebook for the latest news, spec updates, and release
          announcements.{" "}
        </p>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="EcoFlow Store"
        />

        <p>
          Looking for more EcoFlow content? Check out my{" "}
          <Link href="/?brand=ecoflow">related guides and articles</Link>.
        </p>

        <ButtonDualGroup>
          <ButtonGroup title="DELTA 3800 Gen4">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/delta-3800-gen4?variant=67641627803721"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://link.amazon/B04WTtD8E"
                href="https://www.amazon.com/dp/B0HLVRNZDC/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
          <ButtonGroup title="DELTA 3000 Gen4">
            <ButtonList size="medium">
              <Button
                size="medium"
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/delta-3000-gen4?variant=67651080093769"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                size="medium"
                affiliate="https://link.amazon/B0dmUhkW5"
                href="https://www.amazon.com/dp/B0HLSH5GWK/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
        </ButtonDualGroup>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.titanSeries} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.batteryCompatibility} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.powerSystems} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="EcoFlow DELTA Gen4"
        description="3000 & 3800"
        options={[
          {
            text: "View on EcoFlow",
            icon: "EcoFlow",
            options: [
              {
                text: "DELTA 3800 Gen4",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/delta-3800-gen4?variant=67641627803721",
              },
              {
                text: "DELTA 3000 Gen4",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/delta-3000-gen4?variant=67651080093769",
              },
            ],
          },
          {
            text: "View on Amazon",
            icon: "Amazon",
            secondary: true,
            options: [
              {
                text: "DELTA 3800 Gen4",
                affiliate: "https://link.amazon/B04WTtD8E",
                href: "https://www.amazon.com/dp/B0HLVRNZDC/",
              },
              {
                text: "DELTA 3000 Gen4",
                affiliate: "https://link.amazon/B0dmUhkW5",
                href: "https://www.amazon.com/dp/B0HLSH5GWK/",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
